'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/local-api/client';
import { PlusIcon, XMarkIcon, PlayIcon, ChevronLeftIcon, ChevronRightIcon, PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';

interface Story {
  id: string;
  author_id: string;
  media_url: string;
  media_type: 'image' | 'video';
  caption?: string;
  expires_at: string;
  created_at: string;
  author_name: string;
  author_initials: string;
  author_avatar?: string;
  viewed?: boolean;
}

interface StoryGroup {
  author_id: string;
  author_name: string;
  author_initials: string;
  author_avatar?: string;
  stories: Story[];
  hasUnviewed: boolean;
}

interface StoriesStripProps {
  currentUserId?: string;
}

export default function StoriesStrip({ currentUserId }: StoriesStripProps) {
  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);
  const [activeStoryIdx, setActiveStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [addStoryOpen, setAddStoryOpen] = useState(false);
  const [caption, setCaption] = useState('');
  const [previewFile, setPreviewFile] = useState<{ file: File; url: string; type: 'image' | 'video' } | null>(null);
  const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);

  const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const STORY_DURATION = 5000;

  const fetchStories = useCallback(async () => {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('community_stories')
      .select('*, user_profiles(full_name, avatar_url)')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });

    if (error || !data) return;

    const authorIds = Array.from(new Set(data.map((row: any) => row.author_id).filter(Boolean))) as string[];
    const profilesById = new Map<string, { full_name?: string; avatar_url?: string }>();
    if (authorIds.length > 0) {
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, avatar_url')
        .in('id', authorIds);
      (profiles ?? []).forEach((profile: any) => profilesById.set(profile.id, profile));
    }

    let myViewedIds = new Set<string>();
    if (currentUserId) {
      const { data: views } = await supabase
        .from('story_views')
        .select('story_id')
        .eq('viewer_id', currentUserId);
      if (views) myViewedIds = new Set(views.map((v: any) => v.story_id));
    }
    setViewedIds(myViewedIds);

    const groupMap = new Map<string, StoryGroup>();
    data.forEach((row: any) => {
      const authorProfile = profilesById.get(row.author_id) ?? row.user_profiles;
      const name = authorProfile?.full_name || 'Community Member';
      const initials = name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
      const avatar = authorProfile?.avatar_url || undefined;
      const story: Story = {
        id: row.id,
        author_id: row.author_id,
        media_url: row.media_url,
        media_type: row.media_type,
        caption: row.caption,
        expires_at: row.expires_at,
        created_at: row.created_at,
        author_name: name,
        author_initials: initials,
        author_avatar: avatar,
        viewed: myViewedIds.has(row.id),
      };
      if (!groupMap.has(row.author_id)) {
        groupMap.set(row.author_id, {
          author_id: row.author_id,
          author_name: name,
          author_initials: initials,
          author_avatar: avatar,
          stories: [],
          hasUnviewed: false,
        });
      }
      const group = groupMap.get(row.author_id)!;
      group.stories.push(story);
      if (!story.viewed) group.hasUnviewed = true;
    });

    const groups = Array.from(groupMap.values());
    if (currentUserId) {
      const myIdx = groups.findIndex((g) => g.author_id === currentUserId);
      if (myIdx > 0) {
        const [mine] = groups.splice(myIdx, 1);
        groups.unshift(mine);
      }
    }
    setStoryGroups(groups);
  }, [currentUserId]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  useEffect(() => {
    if (!viewerOpen) return;
    setProgress(0);
    if (progressTimer.current) clearInterval(progressTimer.current);

    const currentStory = storyGroups[activeGroupIdx]?.stories[activeStoryIdx];
    if (!currentStory) return;

    if (currentUserId && !viewedIds.has(currentStory.id)) {
      const supabase = createClient();
      supabase.from('story_views').insert({ story_id: currentStory.id, viewer_id: currentUserId }).then(() => {
        setViewedIds((prev) => new Set([...prev, currentStory.id]));
      });
    }

    const interval = 50;
    const steps = STORY_DURATION / interval;
    let step = 0;
    progressTimer.current = setInterval(() => {
      step++;
      setProgress((step / steps) * 100);
      if (step >= steps) {
        clearInterval(progressTimer.current!);
        goNext();
      }
    }, interval);

    return () => { if (progressTimer.current) clearInterval(progressTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerOpen, activeGroupIdx, activeStoryIdx]);

  const openViewer = (groupIdx: number, storyIdx = 0) => {
    setActiveGroupIdx(groupIdx);
    setActiveStoryIdx(storyIdx);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    if (progressTimer.current) clearInterval(progressTimer.current);
    setProgress(0);
  };

  const goNext = useCallback(() => {
    const group = storyGroups[activeGroupIdx];
    if (!group) return;
    if (activeStoryIdx < group.stories.length - 1) {
      setActiveStoryIdx((i) => i + 1);
    } else if (activeGroupIdx < storyGroups.length - 1) {
      setActiveGroupIdx((g) => g + 1);
      setActiveStoryIdx(0);
    } else {
      closeViewer();
    }
  }, [storyGroups, activeGroupIdx, activeStoryIdx]);

  const goPrev = useCallback(() => {
    if (activeStoryIdx > 0) {
      setActiveStoryIdx((i) => i - 1);
    } else if (activeGroupIdx > 0) {
      const prevGroup = storyGroups[activeGroupIdx - 1];
      setActiveGroupIdx((g) => g - 1);
      setActiveStoryIdx(prevGroup.stories.length - 1);
    }
  }, [storyGroups, activeGroupIdx, activeStoryIdx]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.type.startsWith('video') ? 'video' : 'image';
    const url = URL.createObjectURL(file);
    setPreviewFile({ file, url, type });
    setAddStoryOpen(true);
  };

  const handlePostStory = async () => {
    if (!previewFile || !currentUserId) return;
    setUploading(true);
    setUploadError(null);
    try {
      const supabase = createClient();
      const ext = previewFile.file.name.split('.').pop();
      const path = `stories/${currentUserId}/${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from('community-media')
        .upload(path, previewFile.file, { upsert: true });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from('community-media').getPublicUrl(path);
      const publicUrl = urlData.publicUrl;

      const { error: insertError } = await supabase.from('community_stories').insert({
        author_id: currentUserId,
        media_url: publicUrl,
        media_type: previewFile.type,
        caption: caption.trim() || null,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      });

      if (insertError) throw insertError;

      setAddStoryOpen(false);
      setPreviewFile(null);
      setCaption('');
      await fetchStories();
    } catch (err: any) {
      const msg = err?.message || 'Failed to upload story. Please try again.';
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const currentGroup = storyGroups[activeGroupIdx];
  const currentStory = currentGroup?.stories[activeStoryIdx];

  if (storyGroups.length === 0 && !currentUserId) return null;

  return (
    <>
      {/* Stories Strip — no outer box, larger cards */}
      <div className="overflow-x-auto scrollbar-none">
        <div className="flex gap-3 pb-2 min-w-0">

          {/* Add Story Button */}
          {currentUserId && (
            <div className="flex-shrink-0 flex flex-col items-center gap-1.5 cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
              <div className="relative w-[100px] h-[160px] rounded-xl overflow-hidden bg-muted border border-border group-hover:border-primary/50 transition-all">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-muted to-muted/50" />
                {/* Plus button at bottom */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center ring-2 ring-card shadow-lg">
                    <PlusIcon className="text-white" style={{ width: 20, height: 20 }} />
                  </div>
                </div>
              </div>
              <span className="text-xs font-semibold text-foreground text-center w-[100px] truncate">Add Story</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/webm"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          )}

          {/* Story Bubbles — Facebook card style */}
          {storyGroups.map((group, gIdx) => (
            <div key={group.author_id} className="flex-shrink-0 flex flex-col items-center gap-1.5 cursor-pointer group" onClick={() => openViewer(gIdx)}>
              <div className="relative w-[100px] h-[160px] rounded-xl overflow-hidden">
                {/* Thumbnail */}
                {group.stories[0].media_type === 'video' ? (
                  <video
                    src={group.stories[0].media_url}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={group.stories[0].media_url}
                    alt={`${group.author_name}'s story`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                )}
                {/* Bottom gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/50" />
                {/* Avatar with ring */}
                <div className={`absolute top-2 left-1/2 -translate-x-1/2 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center text-white font-bold text-sm ring-[3px] ring-card ${group.hasUnviewed ? 'ring-offset-1 ring-offset-card' : ''}`}
                  style={group.hasUnviewed ? { background: 'linear-gradient(135deg, #1877f2, #42b72a)' } : { background: 'rgba(100,100,100,0.8)' }}>
                  {group.author_avatar ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={group.author_avatar}
                      alt={group.author_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    group.author_initials
                  )}
                </div>
                {/* Video indicator */}
                {group.stories[0].media_type === 'video' && (
                  <div className="absolute bottom-2 right-2">
                    <PlayIcon className="text-white drop-shadow" style={{ width: 14, height: 14 }} />
                  </div>
                )}
                {/* Unviewed ring overlay */}
                {group.hasUnviewed && (
                  <div className="absolute inset-0 rounded-xl ring-2 ring-blue-500 ring-inset pointer-events-none" />
                )}
              </div>
              <span className="text-xs font-semibold text-foreground text-center w-[100px] truncate">
                {group.author_id === currentUserId ? 'Your Story' : group.author_name.split(' ')[0]}
              </span>
            </div>
          ))}

          {/* Empty state */}
          {storyGroups.length === 0 && currentUserId && (
            <div className="flex items-center gap-2 ml-2 text-muted-foreground py-4">
              <PhotoIcon style={{ width: 16, height: 16 }} />
              <span className="text-xs">No stories yet — be the first to share!</span>
            </div>
          )}
        </div>
      </div>

      {/* Add Story Modal */}
      {addStoryOpen && previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="text-sm font-bold text-foreground">Create Story</h3>
              <button
                onClick={() => { setAddStoryOpen(false); setPreviewFile(null); setCaption(''); setUploadError(null); }}
                className="w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
              >
                <XMarkIcon className="text-muted-foreground" style={{ width: 16, height: 16 }} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="relative rounded-xl overflow-hidden bg-muted aspect-[9/16] max-h-64">
                {previewFile.type === 'video' ? (
                  <video src={previewFile.url} className="w-full h-full object-cover" muted autoPlay loop playsInline />
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={previewFile.url} alt="Story preview" className="w-full h-full object-cover" />
                )}
                <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 rounded-full px-2 py-0.5">
                  {previewFile.type === 'video' ? (
                    <VideoCameraIcon className="text-white" style={{ width: 11, height: 11 }} />
                  ) : (
                    <PhotoIcon className="text-white" style={{ width: 11, height: 11 }} />
                  )}
                  <span className="text-[10px] text-white font-semibold capitalize">{previewFile.type}</span>
                </div>
              </div>
              <input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption... (optional)"
                maxLength={120}
                className="w-full bg-input border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
              />
              <p className="text-[10px] text-muted-foreground">Story disappears after 24 hours</p>
              {uploadError && (
                <p className="text-[11px] text-red-500 bg-red-500/10 rounded-lg px-3 py-2 border border-red-500/20">
                  {uploadError}
                </p>
              )}
            </div>
            <div className="flex gap-2 px-4 pb-4">
              <button
                onClick={() => { setAddStoryOpen(false); setPreviewFile(null); setCaption(''); setUploadError(null); }}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePostStory}
                disabled={uploading}
                className="flex-1 py-2.5 rounded-xl bg-[#1877f2] text-white text-sm font-semibold hover:bg-[#166fe5] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <PlusIcon style={{ width: 14, height: 14 }} />
                )}
                Share Story
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Viewer — Facebook full-screen style */}
      {viewerOpen && currentStory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="relative w-full h-full max-w-sm mx-auto flex flex-col">
            {/* Progress bars */}
            <div className="absolute top-0 left-0 right-0 z-10 flex gap-1 p-2 pt-3">
              {currentGroup.stories.map((s, i) => (
                <div key={s.id} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-none"
                    style={{
                      width: i < activeStoryIdx ? '100%' : i === activeStoryIdx ? `${progress}%` : '0%',
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Header */}
            <div className="absolute top-5 left-0 right-0 z-10 flex items-center gap-2 px-3 pt-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-white font-bold text-xs ring-2 ring-white/30">
                {currentGroup.author_initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-bold truncate drop-shadow">{currentGroup.author_name}</p>
                <p className="text-white/60 text-[10px]">
                  {new Date(currentStory.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                onClick={closeViewer}
                className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <XMarkIcon className="text-white" style={{ width: 18, height: 18 }} />
              </button>
            </div>

            {/* Media */}
            <div className="w-full h-full flex items-center justify-center bg-black">
              {currentStory.media_type === 'video' ? (
                <video
                  ref={videoRef}
                  key={currentStory.id}
                  src={currentStory.media_url}
                  className="w-full h-full object-contain"
                  autoPlay
                  playsInline
                />
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  key={currentStory.id}
                  src={currentStory.media_url}
                  alt={currentStory.caption || `${currentGroup.author_name}'s story`}
                  className="w-full h-full object-contain"
                />
              )}
            </div>

            {/* Caption */}
            {currentStory.caption && (
              <div className="absolute bottom-16 left-0 right-0 z-10 px-4">
                <p className="text-white text-sm text-center drop-shadow-lg bg-black/30 rounded-xl px-3 py-2 backdrop-blur-sm">
                  {currentStory.caption}
                </p>
              </div>
            )}

            {/* Navigation tap zones */}
            <button
              onClick={goPrev}
              className="absolute left-0 top-0 bottom-0 w-1/3 z-20 flex items-center justify-start pl-2 opacity-0 hover:opacity-100 transition-opacity"
              aria-label="Previous story"
            >
              <div className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                <ChevronLeftIcon className="text-white" style={{ width: 18, height: 18 }} />
              </div>
            </button>
            <button
              onClick={goNext}
              className="absolute right-0 top-0 bottom-0 w-1/3 z-20 flex items-center justify-end pr-2 opacity-0 hover:opacity-100 transition-opacity"
              aria-label="Next story"
            >
              <div className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center">
                <ChevronRightIcon className="text-white" style={{ width: 18, height: 18 }} />
              </div>
            </button>

            {/* Group navigation dots */}
            {storyGroups.length > 1 && (
              <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center gap-1.5">
                {storyGroups.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setActiveGroupIdx(i); setActiveStoryIdx(0); }}
                    className={`h-1.5 rounded-full transition-all ${i === activeGroupIdx ? 'bg-white w-4' : 'bg-white/40 w-1.5'}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
